import {Injectable} from '@angular/core';

interface DeviceData {
  key: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceInfoService {
  private readonly DEVICE_ID_KEY = 'device_uuid';
  private readonly DEVICE_NAME_KEY = 'device_name';
  private readonly DB_NAME = 'XocialAgentDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'deviceInfo';

  private cached_id_key!: string;
  private cached_name_key!: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor() {
    this.initializeDB();
  }

  private async initializeDB(): Promise<void> {
    if (!this.isIndexedDBSupported()) {
      console.warn('IndexedDB not supported, falling back to localStorage');
      return;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'key' });
          store.createIndex('key', 'key', { unique: true });
        }
      };
    });
  }

  private isIndexedDBSupported(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  private async getFromIndexedDB(key: string): Promise<string | null> {
    if (!this.dbPromise) return null;

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.value : null);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error reading from IndexedDB:', error);
      return null;
    }
  }

  private async setToIndexedDB(key: string, value: string): Promise<boolean> {
    if (!this.dbPromise) return false;

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.put({ key, value });
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error writing to IndexedDB:', error);
      return false;
    }
  }

  private async getStoredValue(key: string): Promise<string | null> {
    // Try IndexedDB first
    if (this.isIndexedDBSupported()) {
      const value = await this.getFromIndexedDB(key);
      if (value) return value;
    }

    // Fallback to localStorage
    return localStorage.getItem(key);
  }

  private async setStoredValue(key: string, value: string): Promise<void> {
    // Try IndexedDB first
    if (this.isIndexedDBSupported()) {
      const success = await this.setToIndexedDB(key, value);
      if (success) return;
    }

    // Fallback to localStorage
    localStorage.setItem(key, value);
  }

  /*
    * Returns the device UUID.
    * If it does not exist, it generates a new one and stores it persistently.
  */
  async getDeviceUUID(): Promise<string> {
    // 1) Fast-path: memory cache so multiple calls in one tick are cheap
    if (this.cached_id_key) return this.cached_id_key;

    // 2) Look in persistent storage
    const stored = await this.getStoredValue(this.DEVICE_ID_KEY);
    if (stored) return (this.cached_id_key = stored);

    // 3) Generate a new UUID
    const uuid = crypto.randomUUID();

    // 4) Store it persistently
    await this.setStoredValue(this.DEVICE_ID_KEY, uuid);

    return (this.cached_id_key = uuid);
  }

  /*
    * Returns the device name.
    * If it does not exist, it determines the device type and stores it persistently.
    * This is used to identify the device type in the backend.
  */
  async getDeviceName(): Promise<string> {
    // 1) Fast-path: memory cache so multiple calls in one tick are cheap
    if (this.cached_name_key) return this.cached_name_key;

    // 2) Look in persistent storage
    const stored = await this.getStoredValue(this.DEVICE_NAME_KEY);
    if (stored) return (this.cached_name_key = stored);

    const userAgent = navigator?.userAgent;
    // simply to Windows, MacOS, Linux, Android, iOS
    const deviceName = /Windows/.test(userAgent) ? 'Windows' :
      /Mac/.test(userAgent) ? 'MacOS' :
      /Linux/.test(userAgent) ? 'Linux' :
      /Android/.test(userAgent) ? 'Android' :
      /iPhone|iPad|iPod/.test(userAgent) ? 'iOS' :
      'Unknown Device';

    // 3) Store it persistently
    await this.setStoredValue(this.DEVICE_NAME_KEY, deviceName);

    return (this.cached_name_key = deviceName);
  }

  /*
    * Synchronous methods for backward compatibility
    * These will return cached values or empty string if not yet loaded
  */
  getDeviceUUIDSync(): string {
    return this.cached_id_key || '';
  }

  getDeviceNameSync(): string {
    return this.cached_name_key || '';
  }

  /*
    * Initialize both values at once for better performance
  */
  async initializeDeviceInfo(): Promise<{ uuid: string; name: string }> {
    const [uuid, name] = await Promise.all([
      this.getDeviceUUID(),
      this.getDeviceName()
    ]);

    return { uuid, name };
  }

  cleanupDeviceInfo(): void {
    this.cached_id_key = '';
    this.cached_name_key = '';
    if (this.dbPromise) {
      this.dbPromise.then(db => {
        const transaction = db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        store.clear();
      }).catch(error => console.error('Error clearing IndexedDB:', error));
    }
    localStorage.removeItem(this.DEVICE_ID_KEY);
    localStorage.removeItem(this.DEVICE_NAME_KEY);
  }
}
